import React, { useState, useEffect } from 'react';
import { t } from '../i18n';

function Ledger({ user }) {
  const [data, setData] = useState({ transactions: [], total_sent: 0, total_received: 0, balance: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (user) fetchLedger();
  }, [user]);

  const fetchLedger = async () => {
    try {
      const res = await fetch('/api/ledger/', {
        headers: { 'Authorization': `Token ${user.token}` }
      });
      const d = await res.json();
      setData(d);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  if (!user) return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h2 style={{ textAlign: 'center', color: '#6200ea' }}>🔑 Login Required</h2>
        <p style={{ textAlign: 'center', color: '#666' }}>{t('lg_login')}</p>
      </div>
    </div>
  );

  const filtered = filter === 'all' ? data.transactions :
    data.transactions.filter(t => t.type === filter);

  const typeConfig = {
    tip_sent: { label: 'Tip Sent', color: '#ff4444', icon: '💸', sign: '-' },
    tip_received: { label: 'Tip Received', color: '#10b981', icon: '💰', sign: '+' },
    boost: { label: 'Boost', color: '#0d9488', icon: '🚀', sign: '-' },
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>📒 My Pi Ledger</h1>

      {/* Summary Cards */}
      <div style={styles.summaryGrid}>
        <div style={{ ...styles.summaryCard, borderTop: '4px solid #10b981' }}>
          <div style={{ ...styles.summaryNum, color: '#10b981' }}>+{data.total_received} π</div>
          <div style={styles.summaryLabel}>{t('lg_tips_received')}</div>
        </div>
        <div style={{ ...styles.summaryCard, borderTop: '4px solid #ff4444' }}>
          <div style={{ ...styles.summaryNum, color: '#ff4444' }}>-{data.total_sent} π</div>
          <div style={styles.summaryLabel}>{t('lg_spent')}</div>
        </div>
        <div style={{ ...styles.summaryCard, borderTop: '4px solid #0d9488' }}>
          <div style={{ ...styles.summaryNum, color: '#0d9488' }}>{data.transactions.length}</div>
          <div style={styles.summaryLabel}>{t('lg_total')}</div>
        </div>
      </div>
      <div style={{ backgroundColor: '#f3e5f5', borderRadius: '12px', padding: '12px 16px', marginBottom: '16px', textAlign: 'center', fontSize: '13px', color: '#6200ea' }}>
        ℹ️ This shows PenFlow activity only. Real Pi wallet balance is in your Pi App.
      </div>

      {/* Filter Tabs */}
      <div style={styles.tabs}>
        {['all', 'tip_sent', 'tip_received', 'boost'].map(f => (
          <button key={f} style={{ ...styles.tab, ...(filter === f ? styles.tabActive : {}) }}
            onClick={() => setFilter(f)}>
            {f === 'all' ? '📋 All' : f === 'tip_sent' ? '💸 Sent' : f === 'tip_received' ? '💰 Received' : '🚀 Boost'}
          </button>
        ))}
      </div>

      {/* Transactions */}
      {loading ? <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>{t('loading')}</div> :
        filtered.length === 0 ? (
          <div style={styles.empty}>
            <p style={{ fontSize: '40px' }}>📭</p>
            <p>{t('lg_empty')}</p>
          </div>
        ) : (
          <div style={styles.list}>
            {filtered.map((tx, i) => {
              const cfg = typeConfig[tx.type] || { label: tx.type, color: '#999', icon: '💠', sign: '' };
              return (
                <div key={i} style={styles.txCard}>
                  <div style={styles.txLeft}>
                    <div style={{ ...styles.txIcon, backgroundColor: cfg.color + '22', color: cfg.color }}>
                      {cfg.icon}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={styles.txType}>{cfg.label}</div>
                      <div style={styles.txDesc}>{tx.description}</div>
                      <div style={styles.txDate}>📅 {tx.date}</div>
                      {tx.expires_at && <div style={styles.txDate}>⏰ Expires: {tx.expires_at}</div>}
                      {tx.txid && (
                        <span style={{ color: '#6200ea', fontSize: '10px', wordBreak: 'break-all', display: 'block', marginTop: '3px', cursor: 'pointer' }}
                          onClick={() => navigator.clipboard?.writeText(tx.txid).then(() => alert(t('lg_txid_copied')))}>
                          🔗 {tx.txid.substring(0,16)}...{tx.txid.substring(tx.txid.length-8)} 📋
                        </span>
                      )}
                      {tx.payment_id && (
                        <div style={{ color: '#999', fontSize: '10px', marginTop: '2px' }}>
                          ID: {tx.payment_id.substring(0,12)}...
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ ...styles.txAmount, color: cfg.color }}>
                    {cfg.sign}{tx.amount} π
                  </div>
                </div>
              );
            })}
          </div>
        )
      }
    </div>
  );
}

const styles = {
  container: { maxWidth: '700px', margin: '20px auto', padding: '20px' },
  title: { color: '#6200ea', marginBottom: '20px' },
  summaryGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '20px' },
  summaryCard: { backgroundColor: 'white', borderRadius: '12px', padding: '16px', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
  summaryNum: { fontSize: '20px', fontWeight: 'bold', color: '#6200ea', marginBottom: '4px' },
  summaryLabel: { fontSize: '12px', color: '#999' },
  tabs: { display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' },
  tab: { padding: '7px 14px', borderRadius: '20px', border: '2px solid #6200ea', backgroundColor: 'white', color: '#6200ea', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' },
  tabActive: { backgroundColor: '#6200ea', color: 'white' },
  list: { display: 'flex', flexDirection: 'column', gap: '10px' },
  txCard: { backgroundColor: 'white', borderRadius: '12px', padding: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
  txLeft: { display: 'flex', gap: '12px', alignItems: 'center' },
  txIcon: { width: '42px', height: '42px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', flexShrink: 0 },
  txType: { fontWeight: 'bold', fontSize: '14px', color: '#333' },
  txDesc: { fontSize: '12px', color: '#666', marginTop: '2px', maxWidth: '200px' },
  txDate: { fontSize: '11px', color: '#999', marginTop: '2px' },
  txAmount: { fontWeight: 'bold', fontSize: '16px', flexShrink: 0 },
  empty: { textAlign: 'center', padding: '40px', backgroundColor: 'white', borderRadius: '12px', color: '#666' },
};

export default Ledger;
